<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Sites &amp; Zones</h5>
        <p class="page-sub">Manage physical locations and their zones for site-scoped visitor tracking</p>
      </div>
      <button class="btn-primary-action" @click="openCreateSite">
        <font-awesome-icon icon="plus" /> Add Site
      </button>
    </div>

    <div v-if="loading" class="sk-table">
      <div v-for="n in 3" :key="n" class="sk-row" style="height:90px;"></div>
    </div>

    <div v-else-if="!sites.length" class="empty-state">
      <font-awesome-icon icon="building" class="empty-icon" />
      <p class="empty-title">No sites configured</p>
      <p class="empty-sub">Add a site to enable zone-level visitor tracking, roll-call by location, and per-site capacity limits.</p>
      <button class="btn-primary-action" @click="openCreateSite">Add First Site</button>
    </div>

    <div v-else class="sites-list">
      <div v-for="site in sites" :key="site.id" class="site-card">
        <div class="site-header">
          <div class="site-info">
            <div class="site-name-row">
              <span class="site-name">{{ site.name }}</span>
              <span class="badge" :class="site.is_active ? 'badge--green' : 'badge--red'">{{ site.is_active ? 'Active' : 'Inactive' }}</span>
              <span v-if="site.capacity" class="badge badge--grey">Cap: {{ site.capacity }}</span>
            </div>
            <p v-if="site.address" class="site-address"><font-awesome-icon icon="map-marker-alt" /> {{ site.address }}</p>
            <p v-if="site.timezone !== 'UTC'" class="site-tz"><font-awesome-icon icon="clock" /> {{ site.timezone }}</p>
          </div>
          <div class="site-actions">
            <button class="btn-ghost-sm" @click="openAddZone(site)"><font-awesome-icon icon="layer-group" /> Add Zone</button>
            <button class="btn-icon" @click="openEditSite(site)"><font-awesome-icon icon="pencil-alt" /></button>
            <button class="btn-icon btn-icon--red" @click="confirmDeleteSite(site)"><font-awesome-icon icon="trash" /></button>
          </div>
        </div>
        <div v-if="site.zones && site.zones.length" class="zone-list">
          <div v-for="z in site.zones" :key="z.id" class="zone-row">
            <div class="zone-info">
              <font-awesome-icon icon="layer-group" class="zone-icon" />
              <span class="zone-name">{{ z.name }}</span>
              <span v-if="z.restricted" class="badge badge--amber">Restricted</span>
              <span v-if="z.capacity"   class="badge badge--grey">Cap: {{ z.capacity }}</span>
            </div>
            <div style="display:flex;gap:6px;">
              <button class="btn-icon" @click="openEditZone(site, z)"><font-awesome-icon icon="pencil-alt" /></button>
              <button class="btn-icon btn-icon--red" @click="confirmDeleteZone(site, z)"><font-awesome-icon icon="trash" /></button>
            </div>
          </div>
        </div>
        <div v-else class="zone-empty">No zones — add zones to group visitors by area within this site.</div>
      </div>
    </div>

    <!-- Site Modal -->
    <div v-if="siteModal" class="modal-backdrop" @mousedown.self="closeSiteModal">
      <div class="modal-box">
        <div class="modal-header">
          <h6 class="modal-title">{{ editingSite ? 'Edit Site' : 'New Site' }}</h6>
          <button class="modal-close" @click="closeSiteModal"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <label class="form-label">Site Name <span class="req">*</span></label>
            <input v-model="siteForm.name" class="form-input" maxlength="100" placeholder="e.g. Head Office" />
          </div>
          <div class="form-row" style="margin-top:14px;">
            <label class="form-label">Address</label>
            <textarea v-model="siteForm.address" class="form-input" rows="2" style="resize:vertical;" placeholder="Full address"></textarea>
          </div>
          <div class="two-col" style="margin-top:14px;">
            <div class="form-row">
              <label class="form-label">Timezone</label>
              <input v-model="siteForm.timezone" class="form-input" placeholder="UTC" />
            </div>
            <div class="form-row">
              <label class="form-label">Capacity</label>
              <input v-model.number="siteForm.capacity" type="number" min="1" class="form-input" placeholder="Unlimited" />
            </div>
          </div>
          <!-- Geofence -->
          <div style="margin-top:18px;">
            <div class="form-label" style="margin-bottom:8px;">Mobile Check-In Geofence <span style="font-size:11px;font-weight:400;color:#aab8cc;">(optional)</span></div>
            <div class="three-col" style="gap:10px;">
              <div class="form-row">
                <label class="form-label">Latitude</label>
                <input v-model.number="siteForm.latitude" type="number" step="any" class="form-input" placeholder="e.g. 51.5074" />
              </div>
              <div class="form-row">
                <label class="form-label">Longitude</label>
                <input v-model.number="siteForm.longitude" type="number" step="any" class="form-input" placeholder="e.g. -0.1278" />
              </div>
              <div class="form-row">
                <label class="form-label">Radius (m)</label>
                <input v-model.number="siteForm.geofence_radius_meters" type="number" min="50" max="5000" class="form-input" placeholder="200" />
              </div>
            </div>
            <span class="form-hint" style="margin-top:4px;display:block;">When set, visitors using the mobile check-in link must be within this distance of the site coordinates.</span>
          </div>

          <div class="toggle-row" style="margin-top:14px;">
            <div class="toggle-info"><span class="toggle-name">Active</span></div>
            <label class="toggle-switch"><input type="checkbox" v-model="siteForm.is_active" /><span class="toggle-track"></span></label>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="closeSiteModal">Cancel</button>
          <button class="btn-primary-action" @click="saveSite" :disabled="savingSite">
            <font-awesome-icon v-if="savingSite" icon="spinner" spin />
            {{ savingSite ? 'Saving…' : (editingSite ? 'Save Changes' : 'Create Site') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Zone Modal -->
    <div v-if="zoneModal" class="modal-backdrop" @mousedown.self="closeZoneModal">
      <div class="modal-box modal-box--sm">
        <div class="modal-header">
          <h6 class="modal-title">{{ editingZone ? 'Edit Zone' : 'Add Zone' }} — {{ zoneParentSite?.name }}</h6>
          <button class="modal-close" @click="closeZoneModal"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <label class="form-label">Zone Name <span class="req">*</span></label>
            <input v-model="zoneForm.name" class="form-input" maxlength="100" placeholder="e.g. Server Room" />
          </div>
          <div class="two-col" style="margin-top:14px;">
            <div class="form-row">
              <label class="form-label">Capacity</label>
              <input v-model.number="zoneForm.capacity" type="number" min="1" class="form-input" placeholder="Unlimited" />
            </div>
            <div class="form-row" style="justify-content:flex-end;padding-top:22px;">
              <label class="toggle-row">
                <span class="toggle-name">Restricted</span>
                <label class="toggle-switch" style="margin-left:10px;"><input type="checkbox" v-model="zoneForm.restricted" /><span class="toggle-track"></span></label>
              </label>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="closeZoneModal">Cancel</button>
          <button class="btn-primary-action" @click="saveZone" :disabled="savingZone">
            <font-awesome-icon v-if="savingZone" icon="spinner" spin />
            {{ savingZone ? 'Saving…' : (editingZone ? 'Save Zone' : 'Add Zone') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirm -->
    <div v-if="deleteConfirm" class="modal-backdrop" @mousedown.self="deleteConfirm = null">
      <div class="modal-box modal-box--sm">
        <div class="modal-header">
          <h6 class="modal-title">Confirm Delete</h6>
          <button class="modal-close" @click="deleteConfirm = null"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <p style="font-size:13px;color:#4a5e78;">Delete <strong>{{ deleteConfirm.name }}</strong>? This cannot be undone.</p>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="deleteConfirm = null">Cancel</button>
          <button class="btn-danger" @click="doDelete" :disabled="deleting">
            <font-awesome-icon v-if="deleting" icon="spinner" spin />Delete
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { useToast } from 'vue-toastification'

export default {
  name: 'VisitorSites',
  setup() { return { toast: useToast() } },
  data() {
    return {
      loading: true, sites: [],
      siteModal: false, editingSite: null, savingSite: false,
      siteForm: this.blankSite(),
      zoneModal: false, editingZone: null, zoneParentSite: null, savingZone: false,
      zoneForm: this.blankZone(),
      deleteConfirm: null, deleteType: null, deleting: false,
    }
  },
  created() { this.load() },
  methods: {
    blankSite() { return { name: '', address: '', timezone: 'UTC', capacity: null, is_active: true, latitude: null, longitude: null, geofence_radius_meters: 200 } },
    blankZone() { return { name: '', capacity: null, restricted: false } },
    async load() {
      this.loading = true
      try {
        const r = await axios.get('visitors/sites')
        this.sites = r.data.data ?? r.data
      } catch { this.toast.error('Failed to load sites.') }
      finally { this.loading = false }
    },
    openCreateSite() { this.siteForm = this.blankSite(); this.editingSite = null; this.siteModal = true },
    openEditSite(s) { this.editingSite = s; this.siteForm = { name: s.name, address: s.address ?? '', timezone: s.timezone ?? 'UTC', capacity: s.capacity ?? null, is_active: s.is_active, latitude: s.latitude ?? null, longitude: s.longitude ?? null, geofence_radius_meters: s.geofence_radius_meters ?? 200 }; this.siteModal = true },
    closeSiteModal() { this.siteModal = false; this.editingSite = null },
    async saveSite() {
      if (!this.siteForm.name.trim()) { this.toast.error('Site name is required.'); return }
      this.savingSite = true
      try {
        if (this.editingSite) {
          await axios.put(`visitors/sites/${this.editingSite.id}`, this.siteForm)
          this.toast.success('Site updated.')
        } else {
          await axios.post('visitors/sites', this.siteForm)
          this.toast.success('Site created.')
        }
        this.closeSiteModal()
        await this.load()
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to save.') }
      finally { this.savingSite = false }
    },
    openAddZone(site) { this.zoneParentSite = site; this.editingZone = null; this.zoneForm = this.blankZone(); this.zoneModal = true },
    openEditZone(site, z) { this.zoneParentSite = site; this.editingZone = z; this.zoneForm = { name: z.name, capacity: z.capacity ?? null, restricted: z.restricted }; this.zoneModal = true },
    closeZoneModal() { this.zoneModal = false; this.editingZone = null },
    async saveZone() {
      if (!this.zoneForm.name.trim()) { this.toast.error('Zone name is required.'); return }
      this.savingZone = true
      try {
        const sid = this.zoneParentSite.id
        if (this.editingZone) {
          await axios.put(`visitors/sites/${sid}/zones/${this.editingZone.id}`, this.zoneForm)
          this.toast.success('Zone updated.')
        } else {
          await axios.post(`visitors/sites/${sid}/zones`, this.zoneForm)
          this.toast.success('Zone added.')
        }
        this.closeZoneModal()
        await this.load()
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to save.') }
      finally { this.savingZone = false }
    },
    confirmDeleteSite(s) { this.deleteConfirm = s; this.deleteType = 'site' },
    confirmDeleteZone(site, z) { this.deleteConfirm = { ...z, _siteId: site.id }; this.deleteType = 'zone' },
    async doDelete() {
      this.deleting = true
      try {
        if (this.deleteType === 'site') {
          await axios.delete(`visitors/sites/${this.deleteConfirm.id}`)
        } else {
          await axios.delete(`visitors/sites/${this.deleteConfirm._siteId}/zones/${this.deleteConfirm.id}`)
        }
        this.toast.success('Deleted.')
        this.deleteConfirm = null
        await this.load()
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to delete.') }
      finally { this.deleting = false }
    },
  },
}
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }
.btn-primary-action { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
.btn-ghost   { display:inline-flex; align-items:center; gap:6px; height:38px; padding:0 16px; border:1px solid #dde5f0; border-radius:8px; background:#fff; font-size:13px; color:#3d5068; cursor:pointer; font-family:inherit; }
.btn-ghost:hover { background:#f4f7fb; }
.btn-ghost-sm { display:inline-flex; align-items:center; gap:5px; height:32px; padding:0 12px; border:1px solid #dde5f0; border-radius:8px; background:#fff; font-size:12px; color:#3d5068; cursor:pointer; font-family:inherit; }
.btn-ghost-sm:hover { background:#f4f7fb; }
.btn-icon    { display:inline-flex; align-items:center; justify-content:center; width:32px; height:32px; border-radius:8px; border:1px solid #e4eaf2; background:#fff; color:#4a5e78; cursor:pointer; font-size:13px; }
.btn-icon:hover { background:#f4f7fb; }
.btn-icon--red { color:#dc2626; }
.btn-icon--red:hover { background:#fee2e2; border-color:#fca5a5; }
.btn-danger  { display:inline-flex; align-items:center; gap:6px; background:#e53e3e; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-danger:disabled { opacity:.6; cursor:not-allowed; }

.sites-list { display:flex; flex-direction:column; gap:16px; }
.site-card  { background:#fff; border:1px solid #e4eaf2; border-radius:14px; overflow:hidden; }
.site-header { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; padding:16px 20px; }
.site-info   { flex:1; }
.site-name-row { display:flex; align-items:center; gap:8px; margin-bottom:4px; flex-wrap:wrap; }
.site-name   { font-size:15px; font-weight:700; color:#1a2b45; }
.site-address, .site-tz { font-size:12px; color:#7a90b0; margin:2px 0; }
.site-actions { display:flex; gap:8px; align-items:flex-start; flex-shrink:0; }
.zone-list { border-top:1px solid #f0f4f8; }
.zone-row  { display:flex; align-items:center; justify-content:space-between; padding:10px 20px; border-bottom:1px solid #f8fafb; }
.zone-row:last-child { border-bottom:none; }
.zone-info { display:flex; align-items:center; gap:8px; flex:1; }
.zone-icon { color:#7a90b0; font-size:13px; }
.zone-name { font-size:13px; font-weight:600; color:#1a2b45; }
.zone-empty { padding:12px 20px; font-size:12px; color:#aab8cc; font-style:italic; background:#fafbfd; }

.badge { font-size:11px; font-weight:700; padding:2px 8px; border-radius:20px; }
.badge--green  { background:#dcfce7; color:#16a34a; }
.badge--red    { background:#fee2e2; color:#dc2626; }
.badge--amber  { background:#fef3c7; color:#d97706; }
.badge--grey   { background:#f1f5f9; color:#64748b; }

.empty-state { text-align:center; padding:60px 20px; }
.empty-icon  { font-size:48px; color:#dde5f0; margin-bottom:16px; }
.empty-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 8px; }
.empty-sub   { font-size:13px; color:#7a90b0; margin:0 0 20px; max-width:440px; margin-left:auto; margin-right:auto; }

.modal-backdrop { position:fixed; inset:0; background:rgba(10,20,40,.45); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; }
.modal-box { background:#fff; border-radius:16px; width:100%; max-width:480px; max-height:90vh; overflow-y:auto; display:flex; flex-direction:column; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-box--sm { max-width:400px; }
.modal-header { display:flex; align-items:center; justify-content:space-between; padding:18px 22px 14px; border-bottom:1px solid #f0f4f8; }
.modal-title  { font-size:16px; font-weight:700; color:#1a2b45; margin:0; }
.modal-close  { background:none; border:none; font-size:16px; color:#7a90b0; cursor:pointer; padding:4px; }
.modal-body   { padding:22px; flex:1; overflow-y:auto; }
.modal-footer { padding:14px 22px; border-top:1px solid #f0f4f8; display:flex; justify-content:flex-end; gap:10px; }

.form-row  { display:flex; flex-direction:column; gap:5px; }
.form-label { font-size:12px; font-weight:600; color:#4a5e78; }
.form-input { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; box-sizing:border-box; font-family:inherit; }
.form-input:focus { border-color:#004AAD; }
.req { color:#e53e3e; }
.two-col   { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
.three-col { display:grid; grid-template-columns:1fr 1fr 1fr; gap:10px; }
.form-hint { font-size:11px; color:#aab8cc; }
@media(max-width:520px){ .three-col { grid-template-columns:1fr; } }
.toggle-row  { display:flex; align-items:center; gap:10px; }
.toggle-name { font-size:13px; font-weight:600; color:#1a2b45; }
.toggle-switch { position:relative; display:inline-block; width:38px; height:22px; flex-shrink:0; }
.toggle-switch input { opacity:0; width:0; height:0; }
.toggle-track  { position:absolute; cursor:pointer; inset:0; background:#dde5f0; border-radius:22px; transition:.2s; }
.toggle-track::before { position:absolute; content:''; height:16px; width:16px; left:3px; top:3px; background:#fff; border-radius:50%; transition:.2s; }
.toggle-switch input:checked + .toggle-track { background:#004AAD; }
.toggle-switch input:checked + .toggle-track::before { transform:translateX(16px); }

.sk-table { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.sk-row   { background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; border-bottom:1px solid #e4eaf2; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
</style>
